import React, { useState } from "react";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { Icon, IconName, useDeviceTypes } from "halifax";
import { CallState } from "redmond";
import { useSelector } from "react-redux";
import styles from "./styles.module.scss";
import {
  BOOK_REASON_HEADING,
  BOOK_REASONS,
  ERROR_MESSAGE,
  SUBMIT_CTA_SUBMITTING_TEXT,
  SUBMIT_CTA_TEXT,
  SUBMIT_SUCCESS_MESSAGE,
  SURVEY_HEADING,
  WOULD_BOOK_ANYWAY_HEADING,
  WOULD_BOOK_ANYWAY_RESPONSES,
} from "./constants";
import { trackEvent } from "../../../../api/v0/trackEvent";
import { getHotelEntryProperties } from "../../../availability/reducer";

export const PackagePostBookingSurvey = () => {
  const { matchesMobile } = useDeviceTypes();

  const hotelEntryProperties = useSelector(getHotelEntryProperties);

  const [errors, setErrors] = useState({
    bookReason: false,
    wouldBookAnyway: false,
  });
  const [formSubmitCallState, setFormSubmitCallState] = useState<CallState>(
    CallState.NotCalled
  );

  const hasErrors = Object.values(errors).some((error) => error);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formElements = form.elements as typeof form.elements & {
      bookReason: HTMLInputElement;
      wouldBookAnyway: HTMLInputElement;
    };

    const bookReason = formElements.bookReason.value;
    const wouldBookAnyway = formElements.wouldBookAnyway.value;

    if (!bookReason) {
      setErrors((prev) => ({ ...prev, bookReason: true }));
    }

    if (!wouldBookAnyway) {
      setErrors((prev) => ({ ...prev, wouldBookAnyway: true }));
    }

    if (bookReason && wouldBookAnyway) {
      setFormSubmitCallState(CallState.InProcess);

      try {
        await trackEvent({
          eventName: "submitted_package_survey",
          properties: {
            booked_reason: bookReason,
            would_book_anyway: wouldBookAnyway,
            los: hotelEntryProperties.properties.los,
            market: hotelEntryProperties.properties.market,
          },
        });

        setFormSubmitCallState(CallState.Success);
      } catch {
        setFormSubmitCallState(CallState.Failed);
      }
    }
  };

  const handleFormChange = () => {
    setErrors({
      bookReason: false,
      wouldBookAnyway: false,
    });
  };

  if (formSubmitCallState === CallState.Success) {
    return (
      <div
        className={clsx(styles["success-container"], {
          [styles.mobile]: matchesMobile,
        })}
      >
        <Icon name={IconName.CheckCircleBorder} className={styles.icon} />
        <Typography variant="h3" className={styles.message}>
          {SUBMIT_SUCCESS_MESSAGE}
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles["package-post-booking-survey"], {
        [styles.mobile]: matchesMobile,
      })}
    >
      <Typography variant="h3" className={styles["survey-heading"]}>
        {SURVEY_HEADING}
      </Typography>
      <form
        onSubmit={handleSubmit}
        className={styles["survey-form"]}
        onChange={handleFormChange}
      >
        <div className={styles["survey-questions"]}>
          <fieldset
            className={clsx(styles.question, {
              [styles["has-error"]]: errors.bookReason,
            })}
          >
            <legend className={styles["question-heading"]}>
              {BOOK_REASON_HEADING}
            </legend>

            {BOOK_REASONS.map((reason) => (
              <div
                className={styles["question-option"]}
                key={`pkg-postbooking-q1-${reason}`}
              >
                <input
                  type="radio"
                  id={reason}
                  name="bookReason"
                  value={reason}
                  className={styles["question-radio-input"]}
                />
                <label htmlFor={reason} className={styles["question-label"]}>
                  {reason}
                </label>
              </div>
            ))}
          </fieldset>

          <fieldset
            className={clsx(styles.question, {
              [styles["has-error"]]: errors.wouldBookAnyway,
            })}
          >
            <legend className={styles["question-heading"]}>
              {WOULD_BOOK_ANYWAY_HEADING}
            </legend>

            {WOULD_BOOK_ANYWAY_RESPONSES.map((response) => (
              <div
                className={styles["question-option"]}
                key={`pkg-postbooking-q2-${response}`}
              >
                <input
                  type="radio"
                  id={response}
                  name="wouldBookAnyway"
                  value={response}
                  className={styles["question-radio-input"]}
                />
                <label htmlFor={response} className={styles["question-label"]}>
                  {response}
                </label>
              </div>
            ))}
          </fieldset>

          {hasErrors && (
            <div className={styles["error-message"]}>{ERROR_MESSAGE}</div>
          )}
        </div>

        <div className={styles["submit-button-wrapper"]}>
          <button
            type="submit"
            className={styles["submit-button"]}
            disabled={formSubmitCallState === CallState.InProcess}
          >
            {formSubmitCallState === CallState.InProcess
              ? SUBMIT_CTA_SUBMITTING_TEXT
              : SUBMIT_CTA_TEXT}
          </button>
        </div>
      </form>
    </div>
  );
};
